import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from "@tanstack/react-table";

import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "src/primitives/Table";
import { cn } from "src/shadcn/lib/utils";

import type { DataTableProps } from "./types";
import { DEFAULT_PAGE_SIZE } from "./constants";
import { getResizeColumnStyle } from "./utils";
import { useTableSort } from "./hooks/useTableSort";
import { useTablePagination } from "./hooks/useTablePagination";
import { useTableSelection } from "./hooks/useTableSelection";
import { useColumnPinning } from "./hooks/useColumnPinning";
import { useColumnVisibility } from "./hooks/useColumnVisibility";
import { useColumnOrdering } from "./hooks/useColumnOrdering";
import { useColumnResize } from "./hooks/useColumnResize";
import { useColumnSizingPersistence } from "./hooks/useColumnSizingPersistence";
import { LoadingOverlay } from "./components/LoadingOverlay";
import { EmptyState } from "./components/EmptyState";
import { SortIndicator } from "./components/SortIndicator";
import { ResizeHandle } from "./components/ResizeHandle";
import { BulkSelectCallout } from "./components/BulkSelectCallout";
import { DataTablePagination } from "./components/DataTablePagination";
import { HeaderCellMenu } from "./components/HeaderCellMenu";
import { HeaderDescription } from "./components/HeaderDescription";

export const DataTable = <TData,>({
  columns,
  data = [],
  getRowId: getRowIdProp,
  sorting: sortingProp,
  onSortingChange: onSortingChangeProp,
  onSort: onSortProp,
  enableSorting = false,
  enableURLSort = true,
  totalCount,
  pageSize = DEFAULT_PAGE_SIZE,
  currentPage: currentPageProp,
  onPageChange: onPageChangeProp,
  enableURLPagination = true,
  enableRowSelection: enableRowSelectionProp,
  selectedRowKeys,
  onRowSelect,
  bulkSelectAllRowsProps,
  enableColumnResize = true,
  persistColumnSizing = false,
  columnSizing: columnSizingProp,
  onColumnSizingChange: onColumnSizingChangeProp,
  enableColumnFreeze = true,
  columnPinning: columnPinningProp,
  onColumnPinningChange: onColumnPinningChangeProp,
  localStorageKeyPrefix,
  columnVisibility: columnVisibilityProp,
  onColumnVisibilityChange: onColumnVisibilityChangeProp,
  onColumnHide: onColumnHideProp,
  enableColumnReorder = false,
  columnOrder: columnOrderProp,
  onColumnOrderChange: onColumnOrderChangeProp,
  enableAddColumn = false,
  onColumnAdd,
  onColumnDelete,
  onColumnUpdate,
  onMoreActionClick,
  enableHeaderMenu,
  loading = false,
  bordered = true,
  className,
  emptyMessage,
  onRowClick,
  allowRowClick = true,
  renderBody,
}: DataTableProps<TData>) => {
  const getRowId = useMemo(
    () =>
      getRowIdProp ??
      ((row: TData) => (row as Record<string, unknown>).id as string),
    [getRowIdProp]
  );

  const { sorting, onSortingChange } = useTableSort({
    sorting: sortingProp,
    onSortingChange: onSortingChangeProp,
    onSort: onSortProp,
    enableURLSort,
  });

  const { currentPage, pageCount, paginationState, onPageChange } =
    useTablePagination({
      totalCount,
      pageSize,
      currentPage: currentPageProp,
      onPageChange: onPageChangeProp,
      enableURLPagination,
    });

  const {
    rowSelection,
    onRowSelectionChange: originalOnRowSelectionChange,
    selectionColumn,
    enableRowSelection,
  } = useTableSelection({
    enableRowSelection: enableRowSelectionProp,
    selectedRowKeys,
    onRowSelect,
    data,
    getRowId,
  });

  // Wrap selection change to detect manual deselection while in bulk mode
  const onRowSelectionChange = useCallback(
    (updaterOrValue: unknown) => {
      if (bulkSelectedRef.current && !isAutoSelectingRef.current) {
        setBulkSelectedAllRowsInternal(false);
        bulkSelectAllRowsProps?.setBulkSelectedAllRows(false);
      }
      originalOnRowSelectionChange(updaterOrValue as never);
    },
    [originalOnRowSelectionChange, bulkSelectAllRowsProps]
  );

  const {
    columnPinning,
    onColumnPinningChange,
    toggleColumnPin,
    isColumnPinned,
  } = useColumnPinning({
    enabled: enableColumnFreeze,
    columnPinning: columnPinningProp,
    onColumnPinningChange: onColumnPinningChangeProp,
    localStorageKeyPrefix,
  });

  const { columnVisibility, onColumnVisibilityChange, hideColumn } =
    useColumnVisibility({
      columnVisibility: columnVisibilityProp,
      onColumnVisibilityChange: onColumnVisibilityChangeProp,
      onColumnHide: onColumnHideProp,
    });

  const { columnOrder, onColumnOrderChange, moveColumn } = useColumnOrdering({
    enabled: enableColumnReorder,
    columnOrder: columnOrderProp,
    onColumnOrderChange: onColumnOrderChangeProp,
  });

  const showHeaderMenu =
    enableHeaderMenu ??
    (enableColumnFreeze ||
      enableAddColumn ||
      !!onColumnHideProp ||
      !!onColumnDelete ||
      !!onColumnUpdate ||
      !!onMoreActionClick);

  const allColumns = useMemo(() => {
    const cols = enableColumnResize
      ? columns.map(col => ({
          ...col,
          minSize:
            (col as { minSize?: number }).minSize ??
            Math.min(150, col.size ?? 150),
        }))
      : columns;

    if (selectionColumn) {
      return [selectionColumn as ColumnDef<TData, unknown>, ...cols];
    }

    return cols;
  }, [columns, selectionColumn, enableColumnResize]);

  const resolvedColumnOrder = useMemo(() => {
    if (columnOrder.length === 0) return [];

    if (selectionColumn && !columnOrder.includes("_selection")) {
      return ["_selection", ...columnOrder];
    }

    return columnOrder;
  }, [columnOrder, selectionColumn]);

  const resolvedColumnPinning = useMemo(() => {
    if (!selectionColumn || !enableColumnFreeze) return columnPinning;

    const left = columnPinning.left ?? [];
    if (left.includes("_selection")) return columnPinning;

    return { ...columnPinning, left: ["_selection", ...left] };
  }, [columnPinning, selectionColumn, enableColumnFreeze]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const {
    columnSizing,
    setColumnSizing,
    initialUserResizedIds,
    persistUserResizedSizing,
  } = useColumnSizingPersistence({
    enabled: enableColumnResize,
    persistToLocalStorage: persistColumnSizing,
    columnSizing: columnSizingProp,
    onColumnSizingChange: onColumnSizingChangeProp,
    localStorageKeyPrefix,
  });

  const table = useReactTable({
    data,
    columns: allColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId,

    manualSorting: true,
    enableSorting,
    state: {
      sorting,
      pagination: paginationState,
      rowSelection,
      columnPinning: resolvedColumnPinning,
      columnVisibility,
      columnOrder: resolvedColumnOrder,
      columnSizing,
    },
    onSortingChange,

    manualPagination: true,
    pageCount,
    onPaginationChange: () => {},

    enableRowSelection,
    onRowSelectionChange,

    enableColumnResizing: enableColumnResize,
    columnResizeMode: "onChange",

    onColumnPinningChange,
    onColumnVisibilityChange,
    onColumnOrderChange,
  });

  const { trackUserResize, lastDataColumnId, clearUserResized } =
    useColumnResize({
      enabled: enableColumnResize,
      table,
      columnVisibility,
      hasSelectionColumn: !!selectionColumn,
      scrollContainerRef,
      columnSizing,
      setColumnSizing,
      initialUserResizedIds,
      onUserResize: persistUserResizedSizing,
    });

  // Wire up user-resize tracking as the column sizing change handler.
  // This must be set after the hook is initialized.
  table.setOptions(prev => ({
    ...prev,
    onColumnSizingChange: trackUserResize,
  }));

  const [bulkSelectedAllRows, setBulkSelectedAllRowsInternal] = useState(false);
  const isAutoSelectingRef = useRef(false);
  const bulkSelectedRef = useRef(false);
  bulkSelectedRef.current = bulkSelectedAllRows;

  // Auto-select all page rows when in bulk mode (handles page navigation)
  useEffect(() => {
    if (bulkSelectedAllRows) {
      isAutoSelectingRef.current = true;
      table.toggleAllPageRowsSelected(true);
      // Reset flag after React processes the state update
      requestAnimationFrame(() => {
        isAutoSelectingRef.current = false;
      });
    }
  }, [bulkSelectedAllRows, data]);

  const isAllPageRowsSelected = table.getIsAllPageRowsSelected();

  const showSelectAllCallout =
    !!bulkSelectAllRowsProps &&
    isAllPageRowsSelected &&
    pageCount > 1 &&
    !bulkSelectedAllRows;
  const showAllSelectedCallout =
    !!bulkSelectAllRowsProps && bulkSelectedAllRows;

  const handleBulkSelectAll = () => {
    setBulkSelectedAllRowsInternal(true);
    bulkSelectAllRowsProps?.setBulkSelectedAllRows(true);
  };

  const handleClearBulkSelection = () => {
    setBulkSelectedAllRowsInternal(false);
    bulkSelectAllRowsProps?.setBulkSelectedAllRows(false);
    table.toggleAllPageRowsSelected(false);
    onRowSelect?.([], []);
  };

  const [scrollState, setScrollState] = useState({
    scrolledStart: false,
    scrolledEnd: false,
  });

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const maxScroll = scrollWidth - clientWidth;
      setScrollState({
        scrolledStart: scrollLeft > 0,
        scrolledEnd: scrollLeft < maxScroll - 1,
      });
    };

    update();
    el.addEventListener("scroll", update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", update);
      resizeObserver.disconnect();
    };
  }, []);

  const handleSort = (columnId: string, desc: boolean) => {
    const isSameSort =
      sorting.length > 0 &&
      sorting[0].id === columnId &&
      sorting[0].desc === desc;

    if (isSameSort) {
      onSortingChange([]);
    } else {
      onSortingChange([{ id: columnId, desc }]);
    }
  };

  const leafColumns = table.getVisibleLeafColumns();
  const lastLeftPinnedId = leafColumns
    .filter(col => col.getIsPinned() === "left")
    .at(-1)?.id;
  const firstRightPinnedId = leafColumns
    .filter(col => col.getIsPinned() === "right")
    .at(0)?.id;

  const hasProportionalSizing =
    enableColumnResize && Object.keys(columnSizing).length > 0;
  const totalDefaultSize = hasProportionalSizing
    ? 0
    : leafColumns
        .filter(col => col.id !== "_selection")
        .reduce((sum, col) => sum + (col.columnDef.size ?? 150), 0);

  // Must track the *current* sizes, not the declared ones: a resize has to widen
  // the table (and scroll the container) rather than be absorbed by the other
  // columns.
  const tableMinWidth = renderBody
    ? leafColumns.reduce((sum, c) => sum + c.getSize(), 0)
    : undefined;

  return (
    <div className={cn("relative flex min-h-0 w-full flex-col", className)}>
      {loading && <LoadingOverlay />}
      {(showSelectAllCallout || showAllSelectedCallout) && (
        <BulkSelectCallout
          bulkSelectAllRowsProps={bulkSelectAllRowsProps!}
          isAllPageRowsSelected={isAllPageRowsSelected}
          isBulkAllSelected={bulkSelectedAllRows}
          onBulkSelectAll={handleBulkSelectAll}
          onClearBulkSelection={handleClearBulkSelection}
        />
      )}
      <div
        ref={scrollContainerRef}
        data-scrolled-start={scrollState.scrolledStart || undefined}
        data-scrolled-end={scrollState.scrolledEnd || undefined}
        className={cn(
          "group/scroll min-h-0 flex-1 overflow-auto",
          bordered && "rounded-lg border"
        )}
      >
        <Table
          className={renderBody ? "table-fixed" : undefined}
          style={tableMinWidth ? { minWidth: tableMinWidth } : undefined}
        >
          {renderBody && (
            <colgroup>
              {leafColumns.map(col => (
                <col
                  key={col.id}
                  // Under table-fixed the browser spreads any difference between
                  // the declared column widths and the table width across every
                  // sized column. Leaving one column auto makes it the only one
                  // that takes up (or gives back) the slack, so resizing a
                  // column no longer shrinks its neighbours. This is the same
                  // column the header and body cells leave unsized, so the
                  // colgroup never disagrees with them.
                  style={
                    col.id === lastDataColumnId
                      ? undefined
                      : { width: col.getSize() }
                  }
                />
              ))}
            </colgroup>
          )}
          <TableHeader className="sticky top-0 z-20 bg-muted">
            {table.getHeaderGroups().map(headerGroup => {
              const dataHeaders = headerGroup.headers.filter(
                header => header.column.id !== "_selection"
              );

              return (
                <TableRow key={headerGroup.id} className="hover:bg-muted">
                  {headerGroup.headers.map((header, headerIndex) => {
                    const isPinned = header.column.getIsPinned();
                    const isSelectionCol = header.column.id === "_selection";
                    const columnIndexInData = dataHeaders.findIndex(
                      dataHeader => dataHeader.id === header.id
                    );
                    return (
                      <TableHead
                        key={header.id}
                        className={cn(
                          "group/head relative",
                          header.column.getCanSort() &&
                            "cursor-pointer select-none",
                          isSelectionCol && "!pe-2",
                          isPinned && "sticky z-10 bg-muted",
                          header.column.id === lastLeftPinnedId &&
                            "after:pointer-events-none after:absolute after:inset-y-0 after:-right-2 after:w-2 after:bg-gradient-to-r after:from-black/6 after:to-transparent after:opacity-0 after:transition-opacity after:duration-200 after:content-[''] group-data-[scrolled-start]/scroll:after:opacity-100 dark:after:from-white/6",
                          header.column.id === firstRightPinnedId &&
                            "before:pointer-events-none before:absolute before:inset-y-0 before:-left-2 before:w-2 before:bg-gradient-to-l before:from-black/6 before:to-transparent before:opacity-0 before:transition-opacity before:duration-200 before:content-[''] group-data-[scrolled-end]/scroll:before:opacity-100 dark:before:from-white/6"
                        )}
                        style={getResizeColumnStyle({
                          size: header.getSize(),
                          isSelectionCol,
                          isLastDataCol: header.column.id === lastDataColumnId,
                          enableResize: enableColumnResize,
                          pinned: isPinned,
                          pinnedStart: header.column.getStart("left"),
                          pinnedEnd: header.column.getAfter("right"),
                          percentageWidth: renderBody
                            ? undefined
                            : !hasProportionalSizing && totalDefaultSize > 0
                              ? `${((header.column.columnDef.size ?? 150) / totalDefaultSize) * 100}%`
                              : undefined,
                        })}
                        onClick={
                          header.column.getCanSort()
                            ? header.column.getToggleSortingHandler()
                            : undefined
                        }
                      >
                        {header.isPlaceholder ? null : isSelectionCol ? (
                          flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )
                        ) : (
                          <div className="flex items-center">
                            <div className="me-auto flex min-w-0 items-center">
                              <span className="truncate">
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </span>
                              <HeaderDescription column={header.column} />
                            </div>
                            <SortIndicator column={header.column} />
                            {showHeaderMenu && !isSelectionCol && (
                              <HeaderCellMenu
                                column={header.column}
                                columnIndex={columnIndexInData}
                                enableAddColumn={enableAddColumn}
                                enableColumnFreeze={enableColumnFreeze}
                                isColumnPinned={isColumnPinned(
                                  header.column.id
                                )}
                                canMoveLeft={
                                  headerIndex > (selectionColumn ? 1 : 0)
                                }
                                canMoveRight={
                                  headerIndex < headerGroup.headers.length - 1
                                }
                                onSort={handleSort}
                                onHideColumn={
                                  onColumnHideProp ? hideColumn : undefined
                                }
                                onTogglePin={toggleColumnPin}
                                onAddColumn={onColumnAdd}
                                onDeleteColumn={onColumnDelete}
                                onMoveColumn={
                                  onColumnUpdate
                                    ? (colId, dir) => moveColumn(colId, dir)
                                    : undefined
                                }
                                onMoreActionClick={onMoreActionClick}
                              />
                            )}
                          </div>
                        )}
                        {enableColumnResize && (
                          <ResizeHandle
                            header={header}
                            onReset={clearUserResized}
                          />
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableHeader>
          {renderBody ? (
            renderBody()
          ) : (
            <TableBody className={loading ? "opacity-50" : undefined}>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map(row => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() ? "selected" : undefined}
                    className={cn(
                      "group hover:bg-muted data-[state=selected]:bg-row-selected",
                      allowRowClick && onRowClick && "cursor-pointer"
                    )}
                    onClick={
                      allowRowClick && onRowClick
                        ? event => onRowClick(event, row.original, row.index)
                        : undefined
                    }
                  >
                    {row.getVisibleCells().map(cell => {
                      const isPinned = cell.column.getIsPinned();
                      const isSelCell = cell.column.id === "_selection";

                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            "whitespace-normal break-words",
                            isSelCell && "!pe-2",
                            isPinned &&
                              "sticky z-10 bg-background transition-colors group-hover:bg-muted group-data-[state=selected]:bg-row-selected",
                            cell.column.id === lastLeftPinnedId &&
                              "after:pointer-events-none after:absolute after:inset-y-0 after:-right-2 after:w-2 after:bg-gradient-to-r after:from-black/6 after:to-transparent after:opacity-0 after:transition-opacity after:duration-200 after:content-[''] group-data-[scrolled-start]/scroll:after:opacity-100 dark:after:from-white/6",
                            cell.column.id === firstRightPinnedId &&
                              "before:pointer-events-none before:absolute before:inset-y-0 before:-left-2 before:w-2 before:bg-gradient-to-l before:from-black/6 before:to-transparent before:opacity-0 before:transition-opacity before:duration-200 before:content-[''] group-data-[scrolled-end]/scroll:before:opacity-100 dark:before:from-white/6"
                          )}
                          style={getResizeColumnStyle({
                            size: cell.column.getSize(),
                            isSelectionCol: isSelCell,
                            isLastDataCol: cell.column.id === lastDataColumnId,
                            enableResize: enableColumnResize,
                            pinned: isPinned,
                            pinnedStart: cell.column.getStart("left"),
                            pinnedEnd: cell.column.getAfter("right"),
                            percentageWidth:
                              !hasProportionalSizing && totalDefaultSize > 0
                                ? `${((cell.column.columnDef.size ?? 150) / totalDefaultSize) * 100}%`
                                : undefined,
                          })}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <EmptyState
                  colSpan={allColumns.length}
                  message={emptyMessage}
                />
              )}
            </TableBody>
          )}
        </Table>
      </div>
      {totalCount !== undefined && totalCount > 0 && (
        <DataTablePagination
          currentPage={currentPage}
          pageCount={pageCount}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};
